import React, { useEffect, useRef, useState } from 'react';
import { content, colors } from '../data/content';
import { ShieldCheck, Sparkles, Heart, CheckCircle2 } from 'lucide-react';

const PreservativeFree = ({ language }) => {
  const currentContent = content[language].preservativeFree;
  const isRTL = language === 'ar';
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  return (
    <section
      id="preservative-free"
      ref={sectionRef}
      style={{
        background: `linear-gradient(135deg, #0D7343 0%, #10B981 40%, ${colors.secondary} 100%)`,
        padding: 'clamp(40px, 6vw, 70px) clamp(20px, 4vw, 40px)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.15,
        backgroundImage: `radial-gradient(circle at 20% 80%, ${colors.accent}50 0%, transparent 50%),
                          radial-gradient(circle at 80% 20%, rgba(255,255,255,0.3) 0%, transparent 40%)`
      }}></div>

      {/* Floating Elements */}
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: `${Math.random() * 60 + 30}px`,
            height: `${Math.random() * 60 + 30}px`,
            background: `rgba(16, 185, 129, ${Math.random() * 0.15 + 0.05})`,
            borderRadius: '50%',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            filter: 'blur(15px)',
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`
          }}
        />
      ))}

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 10
      }}>
        {/* Main Content */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 'clamp(16px, 3vw, 24px)'
        }}>
          {/* Badge/Seal */}
          <div style={{
            position: 'relative',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'scale(1)' : 'scale(0.5)',
            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            {/* Outer Glow */}
            <div style={{
              position: 'absolute',
              inset: '-15px',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${colors.accent}40 0%, transparent 70%)`,
              animation: 'pulse 2s ease-in-out infinite'
            }}></div>
            
            {/* Main Badge */}
            <div style={{
              width: 'clamp(100px, 18vw, 130px)',
              height: 'clamp(100px, 18vw, 130px)',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${colors.accent} 0%, #fdd835 100%)`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 30px rgba(0,0,0,0.25), inset 0 -4px 15px rgba(0,0,0,0.1)',
              border: '3px solid rgba(255,255,255,0.4)',
              position: 'relative'
            }}>
              <ShieldCheck 
                size={typeof window !== 'undefined' && window.innerWidth < 768 ? 32 : 40} 
                color="#0D7343" 
                strokeWidth={2.5}
              />
              <span style={{
                fontSize: 'clamp(0.55rem, 1.3vw, 0.7rem)',
                fontWeight: 800,
                color: '#0D7343',
                marginTop: '2px',
                fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {isRTL ? 'طبيعي 100%' : '100% Natural'}
              </span>
            </div>

            {/* Sparkle Effects */}
            <Sparkles 
              size={20} 
              color={colors.accent} 
              style={{ 
                position: 'absolute', 
                top: '0', 
                right: '-8px',
                animation: 'twinkle 1.5s ease-in-out infinite'
              }} 
            />
            <Sparkles 
              size={14} 
              color={colors.accent} 
              style={{ 
                position: 'absolute', 
                bottom: '8px', 
                left: '-12px',
                animation: 'twinkle 2s ease-in-out infinite',
                animationDelay: '0.5s'
              }} 
            />
          </div>

          {/* Main Title */}
          <div style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out',
            transitionDelay: '0.2s'
          }}>
            <h2 style={{
              fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
              fontWeight: 900,
              color: 'white',
              marginBottom: 'clamp(8px, 1.5vw, 14px)',
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
              textShadow: '0 4px 20px rgba(0,0,0,0.3)',
              lineHeight: isRTL ? 1.5 : 1.2
            }}>
              {currentContent.title}
            </h2>
            
            <p style={{
              fontSize: 'clamp(0.9rem, 2vw, 1.15rem)',
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '700px',
              margin: '0 auto',
              lineHeight: isRTL ? 1.8 : 1.6,
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
            }}>
              {currentContent.subtitle}
            </p>
          </div>

          {/* Features List - Desktop: 4 in a row, Mobile: 2x2 grid */}
          <div className="preservative-features-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 'clamp(10px, 1.5vw, 16px)',
            width: '100%',
            maxWidth: '1000px',
            marginTop: 'clamp(12px, 2vw, 24px)'
          }}>
            {currentContent.features.map((feature, index) => (
              <div
                key={index}
                className="preservative-feature-card"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  padding: 'clamp(14px, 2vw, 20px) clamp(10px, 1.5vw, 14px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.6s ease-out',
                  transitionDelay: `${0.4 + index * 0.1}s`,
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: 'clamp(45px, 7vw, 55px)',
                  height: 'clamp(45px, 7vw, 55px)',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${colors.accent} 0%, #fdd835 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: `0 6px 20px ${colors.accent}35`
                }}>
                  <CheckCircle2 size={typeof window !== 'undefined' && window.innerWidth < 768 ? 22 : 28} color="#0D7343" strokeWidth={2.5} />
                </div>
                <span style={{
                  color: 'white',
                  fontSize: 'clamp(0.85rem, 1.8vw, 1rem)',
                  fontWeight: 700,
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  lineHeight: 1.4
                }}>
                  {feature}
                </span>
              </div>
            ))}
          </div>

          {/* Responsive Styles */}
          <style>{`
            @media (max-width: 900px) {
              .preservative-features-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                max-width: 500px !important;
              }
            }
            @media (max-width: 480px) {
              .preservative-features-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                gap: 12px !important;
                max-width: 100% !important;
              }
              .preservative-feature-card {
                padding: 16px 10px !important;
                border-radius: 16px !important;
              }
            }
          `}</style>

          {/* Bottom Message */}
          <div style={{
            marginTop: 'clamp(16px, 3vw, 28px)',
            padding: 'clamp(12px, 2vw, 18px) clamp(20px, 3vw, 32px)',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '100px',
            border: `2px solid ${colors.accent}50`,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.8s ease-out',
            transitionDelay: '0.8s'
          }}>
            <Heart size={20} color={colors.accent} fill={colors.accent} />
            <span style={{
              color: 'white',
              fontSize: 'clamp(0.85rem, 1.8vw, 1.05rem)',
              fontWeight: 700,
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
            }}>
              {currentContent.tagline}
            </span>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.1); opacity: 0.5; }
        }
      `}</style>
    </section>
  );
};

export default PreservativeFree;
